export class Play {
    constructor(
        public id: string,
        public sequence: number,
        public game: string,
        public type: string,
        public homeScore: number,
        public awayScore: number,
        public period: number,
        public clock: string,
        public scoringPlay?: boolean,
        public participant1?: string,
        public participant2?: string,
        public participant3?: string,
    ) {}

    public get time(): Time {
        return new Time(
            this.period,
            minuteStringToSeconds(this.clock),
        );
    }

    public compareTo(other: Play): number {
        return this.time.compareTo(other.time);
    }
}

export class Time {
    constructor(
        public quarter: number,           // e.g. 1, 2, 3, 4, or 5 (OT)
        public secondsRemaining: number,  // how many seconds left in this quarter
    ) {}

    quarterLength() : number {
        return quarterLength(this.quarter);
    }

    /**
     * Subtract one Time from another, returning the difference in seconds.
     *
     * In Kotlin:
     *  fun minus(other: Time): Double {
     *    ...
     *  }
     */
    public minus(other: Time): number {
        if (this.quarter === other.quarter) {
            return other.secondsRemaining - this.secondsRemaining;
        }

        // Sum up the lengths of any quarters between `other.quarter + 1` and `this.quarter - 1`.
        let extraQuarters = 0;
        for (let q = other.quarter + 1; q < this.quarter; q++) {
            // if q < 5 => 12-minute quarter => 12 * 60 = 720
            // else => 5th quarter or beyond => 5-minute period => 5 * 60 = 300
            extraQuarters += quarterLength(q);
        }

        return (this.quarterLength() - this.secondsRemaining)
            + other.secondsRemaining
            + extraQuarters;
    }

    /**
     * Compare this Time to another Time for ordering.
     *
     * If same quarter, compare by secondsRemaining (descending).
     */
    public compareTo(other: Time): number {
        if (this.quarter === other.quarter) {
            // In Kotlin: return other.secondsRemaining.compareTo(this.secondsRemaining)
            return other.secondsRemaining - this.secondsRemaining;
        }
        // if different quarter, compare by quarter number (ascending)
        return this.quarter - other.quarter;
    }

    /**
     * Returns the "MM:SS" format for this Time's secondsRemaining.
     *
     * In Kotlin:
     *   val minutesRemaining: String
     *     get() = secondsRemaining.secondsToMinutes()
     */
    public get minutesRemaining(): string {
        return secondsToMinutes(this.secondsRemaining);
    }

    public toString(): string {
        return `Q${this.quarter} ${this.minutesRemaining}`;
    }

    public secondsElapsed(): number {
        const elapsedQuarterSeconds = this.quarter<5 ? (this.quarter-1)*720 : 4*720 + (this.quarter-5)*300;
        return elapsedQuarterSeconds
            + (this.quarterLength() - this.secondsRemaining);
    }
}

/**
 * Returns the length of a quarter in seconds.
 */
function quarterLength(quarter: number): number {
    return  (quarter < 5) ? 720 : 300;
}

/**
 * Converts a number of seconds to an MM:SS string.
 *
 * Example: 125 -> "2:05"
 *
 * In the Kotlin version, we also replaced ".0", so
 * we keep everything integer-based here.
 */
export function secondsToMinutes(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    // If minutes are zero, we omit them entirely,
    // so "45 seconds" becomes ":45"
    return `${minutes === 0 ? '' : minutes}:${
        seconds < 10 ? '0' : ''
    }${seconds}`;
}

/**
 * Converts a "MM:SS" string (or just a number string) into seconds (number).
 *
 * Example: "1:30" -> 90
 */
export function minuteStringToSeconds(value: string): number {
    if (value.includes(':')) {
        const parts = value.split(':');
        const mins = parseInt(parts[0], 10);
        const secs = parseFloat(parts[1]);
        return mins * 60 + secs;
    } else {
        return parseFloat(value);
    }
}
